import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import styles from "./StockAdvisoryForm.module.css";
import { axiosConfig } from "../../../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import ReactHotToaster from "../../../common/ReactHotToaster";
import MuiModal from "../../../muiComps/MuiModal";
import AdvisorySummaryModal from "./AdvisorySummaryModal";
import MultiPlansSelectCheckBox from "../../../common/MultiPlansSelectCheckBox";
import OneSignals from "../../../../pages/One SIgnals";
import { calculateDays } from "../../../../utils/CalculateDays";
import usePartnersWS from "../../../../hooks/usePartnersWS";

const advisoryTypeOptions = [
  // { value: "SHORT", label: "SHORT" },
  { value: "MEDIUM", label: "MEDIUM" },
  { value: "LONG", label: "LONG" },
];

const parravaCallTypeOptions = [
  { value: "FUNDAMENTAL", label: "FUNDAMENTAL" },
  { value: "TECHNICAL", label: "TECHNICAL" },
];

const pnlBoxStyle = (isPositive) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "6px 10px",
  borderRadius: "6px",
  backgroundColor: isPositive ? "#e6f4ea" : "#fdecea",
  color: isPositive ? "#1a7f3c" : "#c0392b",
  fontSize: "13px",
  border: `1px solid ${isPositive ? "#a8d5b5" : "#f5c6c2"}`,
});

const StockAdvisoryForm = ({
  isEditing,
  setIsEditing,
  advisoryFormInput,
  setAdvisoryFormInput,
  // initialAdvisoryFormInput,
  clearAdvisoryFormInput,
  activeAdvisoryObj,
  exitPriceRef,
  onSuccess,
}) => {
  const { user } = useSelector((state) => state.auth);
  const USERID = user?.userDetails?.id;
  const [selectedPlans, setSelectedPlans] = useState([]); // list of Plans ID that are eligible for particular advisory
  const formattedSelectedPlans = selectedPlans.join(",");

  const [openAdvisorySummaryModal, setOpenAdvisorySummaryModal] =
    useState(false);

  const [symbolsList, setSymbolsList] = useState([]); // all stocks & symbols list
  const [ltp, setLtp] = useState(null);
  const [ltpLoading, setLtpLoading] = useState(false);

  const [fileInput, setFileInput] = useState(null); // pdf file input

  const [livePnl, setLivePnl] = useState(null);
  const [livePnlLoading, setLivePnlLoading] = useState(false);

  // LIVE LTP (websocket)
  const wsSymbol = advisoryFormInput.symbolName;
  const { ltp: wsLtp, isConnected: wsConnected } = usePartnersWS({
    symbol: wsSymbol,
    enabled: !!wsSymbol,
  });
  // Until the first websocket tick arrives for the newly selected symbol, fall back
  // to the already-fetched REST LTP so "Live LTP" isn't blank right after selection
  const liveLtp = wsLtp != null ? wsLtp : (ltp ? parseFloat(ltp) : null);

  const DURATION = {
    MEDIUM: "6-12 MONTHS",
    LONG: "12-36 MONTHS",
  };

  useEffect(() => {
    if (isEditing && exitPriceRef.current) {
      exitPriceRef.current.focus();
    }
  }, [isEditing, exitPriceRef]);

  useEffect(() => {
    const sym = advisoryFormInput.symbolName;
    if (!sym) {
      setLtp(null);
      return;
    }
    let cancelled = false;
    const fetchLtp = async () => {
      setLtpLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: `/get-latest-tick/?symbol=${sym}`,
        });
        if (!cancelled) setLtp(res.data?.data?.ltp ?? null);
      } catch {
        if (!cancelled) setLtp(null);
      } finally {
        if (!cancelled) setLtpLoading(false);
      }
    };
    fetchLtp();
    return () => {
      cancelled = true;
    };
  }, [advisoryFormInput.symbolName]);

  useEffect(() => {
    const advisoryId = activeAdvisoryObj?.id;
    const rawExit = advisoryFormInput.exitPrice;
    const price = parseFloat(rawExit);
    if (!isEditing || !advisoryId || !rawExit || isNaN(price) || price <= 0) {
      setLivePnl(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setLivePnlLoading(true);
        const res = await axiosConfig({
          method: "get",
          url: `/predictions/check-pnl-advisory/`,
          params: { advisory_id: advisoryId, exit_price: price },
        });
        setLivePnl(res.data);
      } catch {
        setLivePnl(null);
      } finally {
        setLivePnlLoading(false);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [advisoryFormInput.exitPrice, activeAdvisoryObj?.id, isEditing]);

  // FIELDS THAT ARE EDITABLE

  // "is_target_hit":true,
  //     "target_hit_date":null,
  //     "is_stoploss_hit":false,
  //     "stoploss_hit_date":null,

  //     "exit_price":null,
  //     "exit_price_time":null,
  //     "profit_loss":"+20%",
  //     "trade_close_in_days":1,
  //     "description":null

  const {
    symbolName,
    advisoryType,
    entryPrice,
    priceRange,
    targetPrice,
    stopLoss,
    description,
    exitPrice,
    profitLoss,
    tradeClosedInDays,
    trade_status,
    cancel_reason,
    call_type,
    bid_type,
    valid_for_parrva,
    parrva_call_type,
    stock_horizon,
  } = advisoryFormInput;

  const ADVISORY_DURATION = DURATION[advisoryFormInput.advisoryType];

  const queryClient = useQueryClient();

  const NUMERIC_FIELDS = ["targetPrice", "stopLoss"];

  const formInputOnChange = (event) => {
    const { name } = event.target;
    const rawValue = event.target.value;
    const value = NUMERIC_FIELDS.includes(name)
      ? rawValue.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
      : rawValue;

    setAdvisoryFormInput((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const handleParravaToggle = (event) => {
    setAdvisoryFormInput((prevState) => ({
      ...prevState,
      valid_for_parrva: event.target.checked,
    }));
  };

  const fileInputOnChange = (event) => {
    setFileInput(event.target.files[0]);
  };

  // FETCH SYMBOLS LIST QUERY
  const symbolsListQuery = useQuery({
    retry: false,
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchInterval: false, // Disable automatic refetching
    queryKey: ["symbols-list"], // dependency
    queryFn: () => fetchSymbolsList(),
    onSuccess: (data) => {
      setSymbolsList(data.response[0].data);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  // POST ADVISORY FORM TO BACKEND
  const postAdvisoryFormMutation = useMutation({
    mutationFn: () =>
      postAdvisoryForm(
        USERID,
        advisoryFormInput,
        ADVISORY_DURATION,
        fileInput,
        formattedSelectedPlans,
      ),
    onSuccess: (data) => {
      toast.success(data?.response);
      queryClient.invalidateQueries(["advisory-details"]);
      setFileInput(null);
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  //   submit advisory form handler
  const handlePublish = () => {
    postAdvisoryFormMutation.mutate();
    setOpenAdvisorySummaryModal(false);
  };

  // EDIT/UPDATE ADVISORY FORM TO BACKEND
  const updateAdvisoryFormMutation = useMutation({
    mutationFn: () =>
      updateAdvisoryForm(
        USERID,
        activeAdvisoryObj.id,
        advisoryFormInput,
        fileInput,
        activeAdvisoryObj,
        // selectedPlans
      ),
    onSuccess: (data) => {
      toast.success(data?.response);
      queryClient.invalidateQueries(["advisory-details"]);
      setFileInput(null);
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  // EDIT / UPDATE ADVISORY FORM
  const handleUpdate = () => {
    updateAdvisoryFormMutation.mutate();
    setOpenAdvisorySummaryModal(false);
    // setIsEditing(false);
  };

  //   submit advisory form handler
  const handleCloseModal = () => {
    setOpenAdvisorySummaryModal(false);
  };

  // CANCEL EDIT HANDLER
  const handleCancelEdit = () => {
    setIsEditing(false);
    clearAdvisoryFormInput();
  };

  const DISABLE_SUBMIT_BTN =
    // !entryPrice ||
    // entryPrice.trim() === "" ||
    // !targetPrice ||
    // targetPrice.trim() === "" ||
    !selectedPlans || selectedPlans.length === 0;
  // !fileInput;

  // const DISABLE_CANCEL_EDIT_BTN = !exitPrice || exitPrice.trim() === "";

  const DISABLE_CANCEL_EDIT_BTN = true;

  // Advisory is always a long/buy recommendation (no Buy/Sell signal here) — so
  // Target must sit above entry and Stoploss must sit below entry, always.
  const handleViewSummary = () => {
    const skipValidation = isEditing && (trade_status === "CLOSED" || trade_status === "CANCEL");
    if (!skipValidation) {
      const baseEntry = isEditing
        ? parseFloat(activeAdvisoryObj?.entry_price)
        : parseFloat(call_type === "CMP" ? ltp : entryPrice);
      const t = parseFloat(targetPrice);
      const sl = parseFloat(stopLoss);

      if (!isNaN(baseEntry) && baseEntry > 0) {
        if (!isNaN(t) && t > 0 && t <= baseEntry) {
          toast.error("Target Price must be greater than Entry Price");
          return;
        }
        if (!isNaN(sl) && sl > 0 && sl >= baseEntry) {
          toast.error("Stop Loss must be less than Entry Price");
          return;
        }
      }
    }
    setOpenAdvisorySummaryModal(true);
  };

  return (
    <div className={styles.StockAdvisoryForm}>
      {/* <h1>StockAdvisoryForm</h1> */}
      {/* {isEditing ? <span className={styles.blinkingText} >Editing!!</span>  : ""} */}

      {/* this wrapper holds multiselect plan list and advisory form */}
      <div className={styles.outerFormWrapper}>
        <div className={styles.formWrapper}>
          <h2>Advisory Form </h2>
          <h4 className={styles.blinkingText}>
            {isEditing ? "Editing!!" : ""}
          </h4>

          <div className={styles.formBody}>
            {/* SYMBOL NAME */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Symbol Name</p>

              <Autocomplete
                disabled={isEditing}
                options={symbolsList.map((item) => item.name)}
                value={symbolName || null}
                onChange={(_, newValue) =>
                  formInputOnChange({ target: { name: "symbolName", value: newValue || "" } })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search symbol..."
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        fontSize: 13,
                        background: "#fff",
                        "& fieldset": { border: "none" },
                      },
                    }}
                  />
                )}
                sx={{ width: "70%" }}
                ListboxProps={{ style: { maxHeight: 220, fontSize: 13 } }}
                noOptionsText="No symbols found"
              />
            </div>
            {/* LTP — only in create mode */}
            {!isEditing && (
              <div className={styles.inputControlBox}>
                <p className={styles.label} style={{ minWidth: 110, flexShrink: 0 }}>LTP</p>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", flex: 1, flexWrap: "wrap" }}>
                  {ltpLoading ? (
                    <span style={{ fontSize: 13, color: "#64748b" }}>Fetching...</span>
                  ) : ltp != null ? (
                    <span style={{ padding: "4px 14px", borderRadius: "6px", fontWeight: 700, fontSize: 14, background: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc" }}>
                      ₹{parseFloat(ltp).toFixed(2)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 13, color: "#94a3b8" }}>—</span>
                  )}
                </div>
              </div>
            )}

            {/* LIVE LTP (websocket) — only in create mode */}
            {!isEditing && (
              <div className={styles.inputControlBox}>
                <p className={styles.label} style={{ minWidth: 110, flexShrink: 0 }}>Live LTP</p>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, flexWrap: "wrap" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: wsConnected ? "#22c55e" : "#cbd5e1", display: "inline-block" }} />
                  <span style={{ fontSize: 13, color: wsConnected && liveLtp != null ? "#016551" : "#94a3b8", fontWeight: 600 }}>
                    {liveLtp != null ? `₹${liveLtp.toFixed(2)}` : "—"}
                  </span>
                </div>
              </div>
            )}

            <fieldset>
              <legend>Select Advisory Type:</legend>

              {advisoryTypeOptions.map((option) => (
                <div>
                  <input
                    disabled={isEditing}
                    style={{
                      margin: "0.8rem",
                    }}
                    type="radio"
                    id={option.value}
                    name="advisoryType"
                    value={option.value}
                    checked={advisoryType === option.value}
                    onChange={formInputOnChange}
                  />
                  <label for={option.value}>{option.label}</label>
                </div>
              ))}
            </fieldset>

            {/* ADVISORY TYPE - hide on edit */}
            {!isEditing && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>DURATION</p>

                {/* DURATION */}
                <div className={styles.advisoryDuration}>
                  <p>{ADVISORY_DURATION}</p>
                </div>
              </div>
            )}

            {/* PRICE RANGE */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Price Range*</p>
              <input
                disabled={isEditing}
                className={styles.entryPriceInput}
                required
                type="text"
                name="priceRange"
                id="priceRange"
                placeholder="eg. ₹125-₹150"
                value={priceRange}
                onChange={formInputOnChange}
              />
            </div>

            {/* STOP LOSS */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Stop Loss</p>
              <input
                disabled={isEditing && trade_status === "CLOSED"}
                type="text"
                name="stopLoss"
                id="stopLoss"
                value={stopLoss}
                onChange={formInputOnChange}
                style={isEditing && trade_status === "CLOSED" ? { backgroundColor: "#f0f0f0" } : {}}
              />
            </div>

            {/* CALL TYPE - BID/CMP (only for POST, not editing) */}
            {!isEditing && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Call Type*</p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1.5rem",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      style={{ marginRight: "0.5rem" }}
                      type="radio"
                      name="call_type"
                      value="BID"
                      checked={call_type === "BID"}
                      onChange={formInputOnChange}
                    />
                    BID
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      style={{ marginRight: "0.5rem" }}
                      type="radio"
                      name="call_type"
                      value="CMP"
                      checked={call_type === "CMP"}
                      onChange={formInputOnChange}
                    />
                    CMP
                  </label>
                </div>
              </div>
            )}

            {/* BID TYPE — only when Call Type is BID, create mode only */}
            {!isEditing && call_type === "BID" && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Bid Type</p>
                <select
                  className={styles.selectedSymbolInput}
                  name="bid_type"
                  value={bid_type || ""}
                  onChange={formInputOnChange}
                >
                  <option value="" disabled>
                    -- Select Bid Type --
                  </option>
                  <option value="UPPER">Upper</option>
                  <option value="DOWN">Down</option>
                </select>
              </div>
            )}

            {/* ENTRY / SUGGESTED PRICE - disabled when CMP selected or editing */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>
                Entry Price{call_type === "BID" && !isEditing ? "*" : ""}
              </p>
              <input
                disabled={isEditing || call_type === "CMP"}
                className={styles.entryPriceInput}
                required={call_type === "BID"}
                type="text"
                name="entryPrice"
                id="entryPrice"
                value={entryPrice}
                onChange={formInputOnChange}
              />
            </div>

            {/* TARGET PRICE */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Target Price*</p>
              <input
                disabled={isEditing && trade_status === "CLOSED"}
                type="text"
                required
                name="targetPrice"
                id="targetPrice"
                value={targetPrice}
                onChange={formInputOnChange}
                style={isEditing && trade_status === "CLOSED" ? { backgroundColor: "#f0f0f0" } : {}}
              />
            </div>

            {/* P/L SUMMARY — create + edit both */}
            {(() => {
              const baseEntry = isEditing
                ? parseFloat(activeAdvisoryObj?.entry_price)
                : parseFloat(call_type === "CMP" ? ltp : entryPrice);

              if (!baseEntry || isNaN(baseEntry)) return null;

              // EXIT mode: show actual P/L based on exit price
              if (isEditing && trade_status === "CLOSED") {
                const ep = parseFloat(exitPrice);
                if (!ep || isNaN(ep)) return null;
                const exitPnl = ((ep - baseEntry) / baseEntry) * 100;
                return (
                  <div className={styles.inputControlBox}>
                    <p className={styles.label}>P/L Summary</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "70%" }}>
                      <div style={pnlBoxStyle(exitPnl >= 0)}>
                        <span style={{ fontSize: 11, opacity: 0.75 }}>Exit P/L</span>
                        <span style={{ fontWeight: 700 }}>
                          {exitPnl >= 0 ? "+" : ""}{exitPnl.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }

              // CREATE / OPEN EDIT mode: show target + stoploss P/L
              const t = parseFloat(targetPrice);
              const sl = parseFloat(stopLoss);
              const rewardPct = !isNaN(t) && t > 0 ? ((t - baseEntry) / baseEntry) * 100 : null;
              const riskPct = !isNaN(sl) && sl > 0 ? ((sl - baseEntry) / baseEntry) * 100 : null;

              if (rewardPct === null && riskPct === null) return null;

              return (
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>P/L Summary</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "70%" }}>
                    {rewardPct !== null && (
                      <div style={pnlBoxStyle(rewardPct >= 0)}>
                        <span style={{ fontSize: 11, opacity: 0.75 }}>Target</span>
                        <span style={{ fontWeight: 700 }}>
                          {rewardPct >= 0 ? "+" : ""}{rewardPct.toFixed(2)}%
                        </span>
                      </div>
                    )}
                    {riskPct !== null && (
                      <div style={pnlBoxStyle(false)}>
                        <span style={{ fontSize: 11, opacity: 0.75 }}>Stoploss Risk</span>
                        <span style={{ fontWeight: 700 }}>{riskPct.toFixed(2)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {isEditing ? (
              <>
                {/* TRADE STATUS */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Trade Status*</p>
                  <select
                    className={styles.selectedSymbolInput}
                    name="trade_status"
                    value={trade_status}
                    onChange={formInputOnChange}
                  >
                    <option value="OPEN">Open</option>
                    <option value="CLOSED">Closed</option>
                    <option value="CANCEL">Cancel</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                {/* CANCEL REASON - visible only when trade_status is CANCEL */}
                {trade_status === "CANCEL" && (
                  <div className={styles.inputControlBox}>
                    <p className={styles.label}>Cancel Reason*</p>
                    <textarea
                      name="cancel_reason"
                      id="cancel_reason"
                      placeholder="Enter reason for cancellation..."
                      value={cancel_reason}
                      onChange={formInputOnChange}
                    ></textarea>
                  </div>
                )}

                {/* EXIT PRICE - disabled until trade_status is closed */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>EXIT Price*</p>
                  <input
                    ref={exitPriceRef}
                    type="text"
                    required
                    disabled={trade_status !== "CLOSED"}
                    name="exitPrice"
                    id="exitPrice"
                    value={exitPrice}
                    onChange={formInputOnChange}
                  />
                </div>

                {/* Live P/L from API */}
                {trade_status === "CLOSED" && exitPrice && (
                  <div className={styles.inputControlBox}>
                    <p className={styles.label}>Live P/L</p>
                    {livePnlLoading ? (
                      <span style={{ fontSize: 12, color: "#64748b" }}>Calculating...</span>
                    ) : livePnl?.response != null ? (
                      (() => {
                        const pnl = Number(livePnl.response);
                        const isGain = pnl >= 0;
                        return (
                          <span style={{
                            padding: "4px 14px", borderRadius: 6, fontWeight: 700, fontSize: 14,
                            background: isGain ? "#dcfce7" : "#fee2e2",
                            color: isGain ? "#16a34a" : "#dc2626",
                            border: `1px solid ${isGain ? "#86efac" : "#fca5a5"}`,
                          }}>
                            {isGain ? "+" : ""}{pnl.toFixed(2)}%
                          </span>
                        );
                      })()
                    ) : null}
                  </div>
                )}

              </>
            ) : null}

            {/* <div className={styles.inputControlBox}>
            <p className={styles.label}>Description</p>
            <textarea
              name="description"
              id="description"
              placeholder="Description for Admin Panel..."
              value={description}
              onChange={formInputOnChange}
            ></textarea>
          </div> */}

            {/* PARRVA CALL TYPE — only relevant once Parrva is enabled */}
            {valid_for_parrva && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Parrva Call Type</p>
                <select
                  className={styles.selectedSymbolInput}
                  name="parrva_call_type"
                  value={parrva_call_type || ""}
                  onChange={formInputOnChange}
                >
                  <option value="" disabled>
                    -- Select Call Type --
                  </option>
                  {parravaCallTypeOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* STOCK HORIZON — trade duration in days, only relevant once Parrva is enabled */}
            {valid_for_parrva && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Stock Horizon (Days)</p>
                <input
                  type="number"
                  min="0"
                  name="stock_horizon"
                  id="stock_horizon"
                  value={stock_horizon || ""}
                  placeholder="Number of days for the trade"
                  onChange={formInputOnChange}
                />
              </div>
            )}

            {/* FILE UPLOAD */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>File*</p>
              <input
                // disabled={isEditing}
                // required
                type="file"
                name="fileInput"
                onChange={fileInputOnChange}
              />
            </div>

            <div className={styles.btnContainer}>
              <button
                // disabled={
                //   isEditing ? DISABLE_CANCEL_EDIT_BTN : DISABLE_SUBMIT_BTN
                // }
                className={styles.submitBtn}
                // onClick={handleSubmitForm}
                onClick={handleViewSummary}
              >
                view summary
              </button>

              <button
                className={styles.clearBtn}
                onClick={clearAdvisoryFormInput}
              >
                clear
              </button>

              {isEditing ? (
                <button
                  // disabled={DISABLE_SUBMIT_BTN}
                  className={styles.cancelEditBtn}
                  onClick={handleCancelEdit}
                >
                  cancel edit
                </button>
              ) : null}
            </div>
          </div>
          {/* formBody end */}
        </div>

        {/* multi plans Select component */}
        <div>
          {/* <MultiPlansSelectList
            selectedPlans={selectedPlans}
            setSelectedPlans={setSelectedPlans}
          /> */}

          {isEditing ? null : (
            <MultiPlansSelectCheckBox
              selectedPlans={selectedPlans}
              setSelectedPlans={setSelectedPlans}
              showParravaToggle={true}
              validForParrva={valid_for_parrva}
              onParravaToggle={handleParravaToggle}
            />
          )}
        </div>
      </div>
      {/* outFormWrapper end DIV */}

      {/* summary modal */}
      <MuiModal
        open={openAdvisorySummaryModal}
        setOpen={setOpenAdvisorySummaryModal}
      >
        <AdvisorySummaryModal
          advisoryFormInput={advisoryFormInput}
          advisoryDuration={ADVISORY_DURATION}
          fileInput={fileInput}
          handlePublish={handlePublish}
          handleUpdate={handleUpdate}
          handleCloseModal={handleCloseModal}
          formattedSelectedPlans={formattedSelectedPlans}
          isEditing={isEditing}
        />
      </MuiModal>

      {/* react hot toast */}
      <ReactHotToaster />
    </div>
  );
};

export default StockAdvisoryForm;

// FETCHER FUNCTION SAMPLE
const fetchSymbolsList = async () => {
  const res = await axiosConfig({
    method: "get",
    url: "/home/symbols/list",
    data: {},
  });

  return res.data;
};

// function for both 'POST' request
const postAdvisoryForm = async (
  user_id,
  advisory_form_input,
  advisory_duration,
  file_input,
  selected_plans,
) => {
  const {
    symbolName,
    advisoryType,
    entryPrice,
    priceRange,
    targetPrice,
    stopLoss,
    description,
    call_type,
    bid_type,
    valid_for_parrva,
    parrva_call_type,
    stock_horizon,
  } = advisory_form_input;

  console.log(file_input, "file input");
  console.log(advisory_form_input, "post advisory_form_input");

  const formData = new FormData();
  formData.append("symbol", symbolName);
  formData.append("entry_price", entryPrice);
  formData.append("price_range", priceRange);
  formData.append("target_price", targetPrice);
  formData.append("stop_loss", stopLoss);
  formData.append("advisory_type", advisoryType);
  formData.append("advisory_duration", advisory_duration);
  formData.append("call_type", call_type);
  formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
  formData.append("valid_for_parrva", !!valid_for_parrva);
  formData.append("parrva_call_type", valid_for_parrva ? parrva_call_type || "" : "");
  formData.append("stock_horizon", stock_horizon || "");
  if (file_input) {
    formData.append("file", file_input);
  }
  formData.append("visible_to", selected_plans);

  const res = await axiosConfig({
    method: "post",
    url: `/predictions/stocks/${user_id}/advisory`,
    data: formData,
  });

  return res.data;
};

// function for patch request (edit form)
const updateAdvisoryForm = async (
  user_id,
  advisory_id,
  advisory_form_input,
  file_input,
  active_advisory_obj,
) => {
  // if (!watchlistId) return;
  console.log(advisory_form_input, "update advisory_form_input");
  console.log(file_input, "file_input1");
  const {
    entryPrice,
    exitPrice,
    stopLoss,
    targetPrice,
    trade_status,
    cancel_reason,
    valid_for_parrva,
    parrva_call_type,
    stock_horizon,
  } = advisory_form_input;
  // const {
  //   is_target_hit,
  //   target_hit_date,
  //   is_stoploss_hit,
  //   stoploss_hit_date,
  //   exit_price_time,
  //   profit_loss,
  //   trade_close_in_days,
  // } = active_advisory_obj;

  let profit_loss = null;
  if (entryPrice && exitPrice) {
    const entry = parseFloat(entryPrice);
    const exit = parseFloat(exitPrice);
    if (!isNaN(entry) && !isNaN(exit) && entry !== 0) {
      const pl = ((exit - entry) / entry) * 100;
      profit_loss = (pl >= 0 ? "+" : "") + pl.toFixed(2);
    }
  }

  // calculate trade close in days
  let tradeCloseInDays = null;
  const exitDate = active_advisory_obj?.exit_price_time
    ? active_advisory_obj.exit_price_time
    : new Date().toISOString().slice(0, 10);
  if (active_advisory_obj?.created_at && exitDate) {
    tradeCloseInDays = calculateDays(
      active_advisory_obj?.created_at,
      active_advisory_obj?.exit_price_time || exitDate,
    );
  }
  const formData = new FormData();
  // formData.append("is_target_hit", true);
  // formData.append("target_hit_date", null);
  // formData.append("is_stoploss_hit", false);
  // formData.append("stoploss_hit_date", null);
  // formData.append("profit_loss", profit_loss);
  // formData.append("trade_close_in_days", tradeCloseInDays);
  // formData.append("description", description);

  // Only send fields that have changed (using String() for consistent comparison)
  if (String(stopLoss) !== String(active_advisory_obj?.stop_loss ?? "")) {
    formData.append("stoploss_price", stopLoss);
  }
  if (String(targetPrice) !== String(active_advisory_obj?.target_price ?? "")) {
    formData.append("target_price", targetPrice);
  }
  if (
    String(trade_status) !== String(active_advisory_obj?.trade_status ?? "")
  ) {
    formData.append("trade_status", trade_status);
  }
  if (!!valid_for_parrva !== !!active_advisory_obj?.valid_for_parrva) {
    formData.append("valid_for_parrva", !!valid_for_parrva);
    formData.append("parrva_call_type", valid_for_parrva ? parrva_call_type || "" : "");
  } else if (
    valid_for_parrva &&
    String(parrva_call_type ?? "") !== String(active_advisory_obj?.parrva_call_type ?? "")
  ) {
    formData.append("parrva_call_type", parrva_call_type || "");
  }
  if (String(stock_horizon ?? "") !== String(active_advisory_obj?.stock_horizon ?? "")) {
    formData.append("stock_horizon", stock_horizon || "");
  }

  // Only send exit_price when trade_status is CLOSED and value changed
  if (
    trade_status === "CLOSED" &&
    String(exitPrice) !== String(active_advisory_obj?.exit_price ?? "")
  ) {
    formData.append("exit_price", exitPrice);
    formData.append("exit_price_time", null);
  }

  // Only send cancel_reason when trade_status is CANCEL and value changed
  if (
    trade_status === "CANCEL" &&
    cancel_reason &&
    String(cancel_reason) !== String(active_advisory_obj?.cancel_reason ?? "")
  ) {
    formData.append("cancel_reason", cancel_reason);
  }

  // if (exitPrice && exitPrice.trim() !== "") {
  //   formData.append("exit", true);
  // }

  if (file_input) {
    formData.append("file", file_input);
  }

  // {
  //       is_target_hit: true,
  //       target_hit_date: null,
  //       is_stoploss_hit: false,
  //       stoploss_hit_date: null,
  //       exit_price: exitPrice, // from formInput
  //       exit_price_time: null,
  //       profit_loss: profit_loss,
  //       trade_close_in_days: tradeCloseInDays,
  //       description: description, // from formInput
  //     },
  const res = await axiosConfig({
    method: "patch",
    // url: "/predictions/advisory/589/9/update",
    url: `/predictions/advisory/${user_id}/${advisory_id}/update`,
    data: formData,
  });

  return res.data;
};
